import { useTranslation } from "react-i18next";
import { useSelfCancellationAllowed } from "@/lib/backend/utils/hooks/use-self-cancellation";

/**
 * The long-form explanations behind the info buttons on the signup, hours and expenses screens.
 *
 * Hooks rather than exported constants, because the text is now translated and `t` has to come from
 * somewhere. Every caller is already a component, so this costs them one line and keeps the
 * alternative — threading `t` down through four call sites — out of the tree.
 *
 * Paragraphs are separate keys (`p1`, `p2`) instead of one array per section. i18next can return an
 * array with `returnObjects`, but then a bundle that loses a paragraph fails silently and the
 * key-parity test has nothing to compare; a key per paragraph is checkable.
 */

type InfoSection = {
  title: string;
  content: string | string[];
};

/** Explains what applying means, and when the schedule becomes final. */
export function useApplicationInfoSections(
  getRevokePeriodText: () => string,
  revokePeriod: number,
  schedulingMethod?: string,
): InfoSection[] {
  const { t } = useTranslation();
  const isAvailabilityBased = schedulingMethod === "AVAILABILITY_BASED";
  // Rechtstreeks uit de hook en niet als vierde parameter: beide aanroepers weten dit al via
  // `useRevokePeriod`, maar het door twee lagen doorgeven maakt de vraag makkelijker te vergeten
  // dan te stellen — en dan belooft dit scherm intrekken dat de api weigert.
  const selfCancellationAllowed = useSelfCancellationAllowed();

  return [
    {
      title: t("info:application.how.title"),
      content: isAvailabilityBased
        ? [
            t("info:application.how.availability.p1"),
            t("info:application.how.availability.p2"),
            t("info:application.how.availability.p3"),
          ]
        : [t("info:application.how.shift.p1"), t("info:application.how.shift.p2")],
    },
    {
      title: isAvailabilityBased
        ? t("info:application.timeslot.title")
        : t("info:application.updates.title"),
      content: isAvailabilityBased
        ? t("info:application.timeslot.content")
        : t("info:application.updates.content"),
    },
    {
      title: t("info:application.updates.title"),
      content: t("info:application.updates.content"),
    },
    // "Kan ik mijn aanmelding intrekken?" wordt bij `NOT_ALLOWED` met nee beantwoord, en dan staat
    // de sectie er ook zonder dagengrens: bij `revokePeriod: 0` zou hij anders wegvallen op precies
    // de instelling die "altijd" betekent, en dat is nu juist niet waar. De deadline-versie mag er
    // dan niet meer staan — die belooft een intrekknop die de api weigert.
    ...(!selfCancellationAllowed
      ? [
          {
            title: t("info:application.revoke.title"),
            content: t("info:application.revoke.not-allowed"),
          },
        ]
      : revokePeriod > 0
        ? [
            {
              title: t("info:application.revoke.title"),
              content: t("info:application.revoke.content", {
                revokePeriodText: getRevokePeriodText(),
              }),
            },
          ]
        : []),
    {
      title: t("info:application.final-schedule.title"),
      content: t("info:application.final-schedule.content"),
    },
  ];
}

/** Explains the expense claim process, its statuses and why a submitted claim is final. */
export function useDeclarationInfoSections(): InfoSection[] {
  const { t } = useTranslation();

  return [
    { title: t("info:declaration.what.title"), content: t("info:declaration.what.content") },
    {
      title: t("info:declaration.approval.title"),
      content: [t("info:declaration.approval.p1"), t("info:declaration.approval.p2")],
    },
    { title: t("info:declaration.edit.title"), content: t("info:declaration.edit.content") },
    {
      title: t("info:declaration.processed.title"),
      content: t("info:declaration.processed.content"),
    },
  ];
}

/**
 * Explains recording hours, and what to do when the office entered them for you.
 *
 * Deze modal ging lang alleen over eigen invoer, en zei dat ingevoerde uren vastliggen. Sinds
 * admin#624 kan de medewerker reageren op uren die het kantoor namens hem invulde, dus die twee
 * dingen spraken elkaar tegen (admin#1093). `edit` gaat nu over beide wegen en `dispute` legt de
 * tweede uit: wat bezwaar doet, dat een toelichting verplicht is, en wat het kantoor ermee kan.
 */
export function useHoursInfoSections(): InfoSection[] {
  const { t } = useTranslation();

  return [
    { title: t("info:hours.what.title"), content: t("info:hours.what.content") },
    {
      title: t("info:hours.approval.title"),
      content: [t("info:hours.approval.p1"), t("info:hours.approval.p2")],
    },
    { title: t("info:hours.edit.title"), content: t("info:hours.edit.content") },
    {
      title: t("info:hours.dispute.title"),
      content: [t("info:hours.dispute.p1"), t("info:hours.dispute.p2")],
    },
    { title: t("info:hours.processed.title"), content: t("info:hours.processed.content") },
  ];
}

/** Explains what counts as a travel expense and what proof has to go with it. */
export function useTravelExpensesInfoSections(): InfoSection[] {
  const { t } = useTranslation();

  return [
    {
      title: t("info:travel-expenses.what.title"),
      content: t("info:travel-expenses.what.content"),
    },
    {
      title: t("info:travel-expenses.documents.title"),
      content: [t("info:travel-expenses.documents.p1"), t("info:travel-expenses.documents.p2")],
    },
    {
      title: t("info:travel-expenses.maximum.title"),
      content: t("info:travel-expenses.maximum.content"),
    },
    {
      title: t("info:travel-expenses.approval.title"),
      content: [t("info:travel-expenses.approval.p1"), t("info:travel-expenses.approval.p2")],
    },
  ];
}
